import invariant from 'invariant';

import { fetchPages } from 'api/jukeContent';

import { fetchNavigation, fetchFooterNavigation, fetchMobileLightNavigation } from 'api/contentfulContent';

import graphql from 'api/graphql';

import { contentActions } from 'actions/actions-const';
import { contentDownloaded } from 'actions/lifecycle-actions';
import { showNotification } from 'actions/notifications-actions';
import { setNavigation, setFooterNavigation, setMobileLightNavigation } from 'actions/navigation-actions';

import { NotificationTypes, NotificationTexts } from 'components/Notifications/Notification';

import { MIN_TILE_SIZES } from 'globalConst/const';
import { CONTENT_TYPES } from 'globalConst/contentTypes-const';
import * as cssVariable from 'cssVariables';
import { isTouch } from 'util/device';
import { parseInitialSearch } from './search-actions';
import logger from 'util/logger';

/**
 * Gets main and footer navigation in non-blocking way.
 * @param {Dispatch} dispatch
 */
function getNavigations(dispatch) {
  fetchNavigation().then((navigationItems) => dispatch(setNavigation(navigationItems)));
  fetchFooterNavigation().then((footerNavigation) => dispatch(setFooterNavigation(footerNavigation)));
  fetchMobileLightNavigation().then((mobileLightNavigation) =>
    dispatch(setMobileLightNavigation(mobileLightNavigation))
  );
}

/**
 * Gets main content: JSONs, random station and for pages.
 * @param {Dispatch} dispatch
 */
async function getMainContent(dispatch) {
  const pPages = fetchPages();

  const [pages] = await Promise.all([pPages]);

  const randomStation = await graphql.fetchRandomStation();

  // The minimum content we need are pages and station,
  // so check on this before dispatching a success
  invariant(pages && pages.length > 0, 'Pages should exists while loading Juke');
  // TODO we doen nu een Object.values, later willen juist objecten hebben in onze store

  // Dispatch the Contentful content first so the pages can start rendering (improved percieved page load)
  dispatch({
    type: contentActions.FETCH_PAGES_SUCCESS,
    pages: Object.values(pages),
  });

  const ranStation = randomStation
    ? {
        station: randomStation.station,
        cta: randomStation.cta,
      }
    : null;

  dispatch(setContent(ranStation, CONTENT_TYPES.RANDOM_STATION));

  dispatch(parseInitialSearch());

  return null;
}

export function downloadContent() {
  return async (dispatch) => {
    try {
      dispatch({ type: contentActions.FETCH_CONTENT_REQUEST });

      getNavigations(dispatch);

      await getMainContent(dispatch);

      dispatch(contentDownloaded());
    } catch (error) {
      logger.error('Something went wrong while downloading and indexing content.', error);
      return dispatch({ type: contentActions.FETCH_CONTENT_FAILURE, error });
    }

    return true;
  };
}

export function isOnline() {
  return async (dispatch) => {
    function checkConnection() {
      if (navigator.onLine) {
        dispatch({ type: contentActions.IS_ONLINE });
      } else {
        dispatch(
          showNotification({
            type: NotificationTypes.ERROR,
            message: NotificationTexts.NO_INTERNET,
          })
        );
        dispatch({ type: contentActions.IS_OFFLINE });
      }
    }

    window.addEventListener('online', checkConnection);
    window.addEventListener('offline', checkConnection);
    window.addEventListener('load', checkConnection);
  };
}

export function setCurrentPageSlug(pageSlug, pageSubSlug) {
  return (dispatch) => {
    dispatch({
      type: contentActions.SET_CURRENT_PAGE_SLUG,
      pageSlug,
      pageSubSlug,
    });
  };
}

export function setTileAmount(contentWidth) {
  return (dispatch) => {
    const tileAmount = {};
    Object.keys(MIN_TILE_SIZES).forEach((key) => {
      const [minWidthTile, minWidthTileMobile] = MIN_TILE_SIZES[key];
      const minWidth = cssVariable.gutter + (isTouch || contentWidth < 500 ? minWidthTileMobile : minWidthTile);
      let visibleItems = 1;
      while (contentWidth / (visibleItems + 1) >= minWidth) {
        visibleItems += 1;
      }
      tileAmount[key] = visibleItems;
    });

    dispatch({
      type: contentActions.SET_TILE_AMOUNT,
      tileAmount,
      contentWidth,
    });
  };
}

/**
 * Store the fetched content in the store
 * @param {slug:{Data}} data - data type e.g the overview collection. It needs to have the slug as key e.g  const data = {}; data[slug] = collection;
 * @param {String} contentType - content type e.g overviewCollection
 */
export function setContent(data, contentType) {
  return (dispatch) => {
    dispatch({
      type: contentActions.FETCH_CONTENT_SUCCESS,
      payload: {
        data,
        type: contentType,
      },
    });
  };
}
